import { useEffect } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { DatePicker, Form, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useProfileTileSave } from '@/components/profile';
import { ROUTES } from '@/constants';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { DATE_FORMATS, formatDateForBackend } from '@/utils/formUtils';
import notifySalaryCleanup from '../../actions/notifySalaryCleanup';
import buildEmployeeProfileBody from '../employeeProfileBody';
import { sectionOfField } from '../profileSections';

/**
 * The save mutation, reset, the submit handler and the effective date field.
 * On the profile page also the open tile's save and discard, handed to the
 * page through `actionsRef` (for the leave-tile question).
 */
export default function useEmployeeFormActions({
  actionsRef,
  baselineRef,
  calculationData,
  company,
  deletedFileIds,
  employeeId,
  employmentChangeDetected,
  form,
  handleAdditionalInfoDirtyChange,
  inSection,
  isEdited,
  isProfile,
  navigate,
  notification,
  onFinishFailed,
  onSaved,
  profileSection,
  queryClient,
  selectedFiles,
  serverData,
  setDeletedFileIds,
  setRefetchCompany,
  setResetTrigger,
  setSelectedFiles,
  setUploadKey,
  t,
}) {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      if (isEdited) {
        return await API.employees.updateEmployeeById(employeeId, payload);
      } else {
        return await API.employees.createNewEmployee(payload);
      }
    },
    onSuccess: (response) => {
      form.resetFields();

      // Reset dirty state and trigger initial values update
      handleAdditionalInfoDirtyChange(false);
      setResetTrigger((prev) => prev + 1);

      notification.success({
        message: t('success', { ns: 'messages' }),
        description: response.message,
      });
      notifySalaryCleanup(notification, t, response);

      // Invalidate employee queries to trigger refetch
      if (isEdited && employeeId) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.employee, employeeId],
        });
      }
      // Also invalidate employees list and sector structure to refresh table and structure modal
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.employees],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.sectorStructure],
      });

      if (onSaved) {
        // The profile page stays open: files already sent must not go again
        // with the next save.
        setSelectedFiles([]);
        setDeletedFileIds([]);
        setUploadKey((k) => k + 1);
        onSaved(response);
        return;
      }

      const navigateUrl = company
        ? `${ROUTES.admin}/${ROUTES.employees}?company=${company}`
        : `${ROUTES.admin}/${ROUTES.employees}`;

      navigate(navigateUrl);
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message,
      });
    },
  });

  const handleReset = () => {
    form.resetFields();
    setRefetchCompany((p) => !p);
    handleAdditionalInfoDirtyChange(false);
    setResetTrigger((prev) => prev + 1);
  };

  // `current`: the whole form store, which the profile compares with the
  // loaded values; a tile save passes it limited to that tile.
  const onFinish = async (values, current = form.getFieldsValue(true)) => {
    // Build new nested objects: `values` keeps the dayjs dates the pickers
    // need, since it goes back into the form after the save.
    const formValues = { ...values };

    if (values?.employmentSettings) {
      const es = values.employmentSettings;
      formValues.employmentSettings = { ...es };
      if (es.startDate) {
        formValues.employmentSettings.startDate = formatDateForBackend(
          es.startDate
        );
      }
      if (es.endDate) {
        formValues.employmentSettings.endDate = formatDateForBackend(
          es.endDate
        );
      }
      if (es.dateOfWorkPermissionForeigner) {
        formValues.employmentSettings.dateOfWorkPermissionForeigner = dayjs(
          es.dateOfWorkPermissionForeigner
        ).format(DATE_FORMATS.STANDARD);
      }
    }

    if (values?.additionalPersonalInfo) {
      const api = values.additionalPersonalInfo;
      const info = { ...api };
      formValues.additionalPersonalInfo = info;

      if (api.birthDate) {
        info.birthDate = formatDateForBackend(
          api.birthDate,
          DATE_FORMATS.STANDARD
        );
      }

      if (api.familyCelebrationDate) {
        info.familyCelebrationDate = formatDateForBackend(
          api.familyCelebrationDate,
          DATE_FORMATS.STANDARD
        );
      }

      if (api.workExperienceStartDate) {
        info.workExperienceStartDate = formatDateForBackend(
          api.workExperienceStartDate,
          DATE_FORMATS.STANDARD
        );
      }

      if (api.previousEmployments) {
        info.previousEmployments = api.previousEmployments.map(
          (employment) => ({
            ...employment,
            startDate: employment.startDate
              ? formatDateForBackend(
                  employment.startDate,
                  DATE_FORMATS.STANDARD
                )
              : null,
            endDate: employment.endDate
              ? formatDateForBackend(employment.endDate, DATE_FORMATS.STANDARD)
              : null,
          })
        );
      }

      if (api.children) {
        info.children = api.children.map((child) => ({
          ...child,
          birthDate: child.birthDate
            ? formatDateForBackend(child.birthDate, DATE_FORMATS.STANDARD)
            : null,
        }));
      }
    }

    // The profile only defines the absence quota (enabled + per-type limits).
    // The day-by-day `entries` array is managed in the calendar. Send the quota
    // as dotted $set paths so a full-object $set never wipes the calendar's
    // entries.
    if (formValues.specialAbsence) {
      const sa = formValues.specialAbsence;
      delete formValues.specialAbsence;
      formValues['specialAbsence.enabled'] = !!sa.enabled;
      formValues['specialAbsence.limits'] = sa.limits || {
        absence: 0,
        sick: 0,
        other: 0,
      };
    }

    // Pull effectiveDate off the top of formValues — it's an audit-only meta
    // field, not an employee property. Send it only when an employment change
    // was actually detected, so unrelated edits never log a career event.
    const rawEffectiveDate = formValues.effectiveDate;
    delete formValues.effectiveDate;

    const payload = {
      ...formValues,
      holidayLeaveCalculation: {
        ...calculationData?.calculation,
        ...formValues.holidayLeaveCalculation,
      },
    };

    if (employmentChangeDetected) {
      payload.effectiveDate = rawEffectiveDate
        ? dayjs(rawEffectiveDate).format(DATE_FORMATS.STANDARD)
        : dayjs().format(DATE_FORMATS.STANDARD);
    }

    if (!payload) return;

    let body = payload;
    // Profile: picked files belong to the Documents tile and go with its save.
    const sendFiles = !isProfile || profileSection === 'dok';
    const files = sendFiles ? selectedFiles : [];
    const deletedIds = sendFiles ? deletedFileIds : [];

    // Profile page: send only the groups that were changed, so saving without
    // changes leaves the document untouched.
    if (isProfile && baselineRef.current) {
      const result = buildEmployeeProfileBody({
        payload,
        current,
        base: baselineRef.current,
        serverData,
      });
      if (!result.hasChanges && !files.length && !deletedIds.length) {
        // Same toast as a save on the old page; nothing to send.
        notification.success({
          message: t('success', { ns: 'messages' }),
          description: t('employeeProfile.noChanges', { ns: 'translations' }),
        });
        onSaved?.();
        return;
      }
      ({ body } = result);
    }

    const formDataToSend = new FormData();

    for (const [key, value] of Object.entries(body)) {
      formDataToSend.append(key, JSON.stringify(value));
    }

    files.forEach((file) => {
      formDataToSend.append('files', file);
    });

    if (deletedIds.length) {
      formDataToSend.append('deletedFileIds', JSON.stringify(deletedIds));
    }

    await mutateAsync(formDataToSend);

    // `values` still holds the picker dates (dayjs), never the sent strings.
    form.setFieldsValue(values);
  };

  // The business date of a contract / working-time change. On the profile it
  // shows on both tiles that can trigger it (Contract and Working time).
  const effectiveDateItem = employmentChangeDetected && (
    <Form.Item
      hidden={isProfile && !inSection('ugovor') && !inSection('vreme')}
      name="effectiveDate"
      label={
        <span className="inline-flex items-center gap-1">
          {t('effectiveDate', { ns: 'forms' })}
          <Tooltip title={t('effectiveDateHelp', { ns: 'forms' })}>
            <InfoCircleOutlined className="text-xs opacity-60" />
          </Tooltip>
        </span>
      }
      className="!mb-1 w-full"
      initialValue={dayjs()}
    >
      <DatePicker
        className="w-full"
        format={DATE_FORMATS.DISPLAY}
        placeholder={t('effectiveDate', { ns: 'forms' })}
      />
    </Form.Item>
  );

  const saveTile = useProfileTileSave({
    form,
    baselineRef,
    sectionOfField,
    section: profileSection,
    onFinish,
    onFinishFailed,
  });

  // Back to the values as loaded (not the raw defaults), files included.
  const discardChanges = () => {
    handleReset();
    if (baselineRef.current) form.setFieldsValue(baselineRef.current);
    setSelectedFiles([]);
    setDeletedFileIds([]);
    setUploadKey((k) => k + 1);
  };

  useEffect(() => {
    if (actionsRef)
      actionsRef.current = { save: saveTile, discard: discardChanges };
  });

  return {
    discardChanges,
    effectiveDateItem,
    handleReset,
    isPending,
    onFinish,
    saveTile,
  };
}
